import { useRef } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { RotateCcw } from "lucide-react";

const VARS = {
  client: { label: "Nom du client", example: "M. Dupont" },
  date: { label: "Date", example: "14/06/2026" },
  adresse: { label: "Adresse", example: "12 rue des Lilas, Avignon" },
  reference: { label: "Réf. chantier", example: "CMD-2026-042" },
  lien: { label: "Lien intervention", example: "https://bati-assistant.fr/sav/intervention/abc" },
  lien_rdv: { label: "Lien confirmer / changer", example: "https://bati-assistant.fr/rdv/Xy12ab" },
  entreprise: { label: "Votre entreprise", example: "Sitex Isolation" },
};

export const SMS_VARS_BY_KIND = {
  sav_client_reminder: ["client", "date", "adresse", "lien_rdv", "entreprise"],
  sav_client_on_the_way: ["client", "adresse", "lien_rdv", "entreprise"],
  sav_worker_assigned: ["client", "date", "adresse", "lien", "entreprise"],
  order_planned_client: ["client", "reference", "adresse", "entreprise"],
};

const GSM = "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";

const GSM_EXT = "€^{}[]~|\\";

const render = (text, orgName) => text
  .replace(/\{(\w+)\}/g, (m, k) => (k === "entreprise" ? orgName || "Bati Assistant" : VARS[k]?.example ?? m))
  .replace(/\s{2,}/g, " ").trim();

const smsCount = (text) => {
  const gsm = [...text].every((c) => GSM.includes(c) || GSM_EXT.includes(c));
  const len = gsm ? [...text].reduce((n, c) => n + (GSM_EXT.includes(c) ? 2 : 1), 0) : text.length;
  const single = gsm ? 160 : 70;
  const multi = gsm ? 153 : 67;
  return len <= single ? 1 : Math.ceil(len / multi);
};

export const SmsTemplateEditor = ({ kind, value, defaultText, onChange, disabled, orgName }) => {
  const ref = useRef(null);
  const caret = useRef(null);
  const text = value || defaultText || "";
  const preview = render(text, orgName);

  const insert = (name) => {
    const el = ref.current;
    const pos = caret.current ?? text.length;
    const next = `${text.slice(0, pos)}{${name}}${text.slice(pos)}`;
    onChange(next);
    caret.current = pos + name.length + 2;
    requestAnimationFrame(() => el?.focus());
  };

  return (
    <div className="space-y-2 mt-3" data-testid={`sms-template-${kind}`}>
      <Textarea ref={ref} rows={3} value={text} disabled={disabled}
        onChange={(e) => { caret.current = e.target.selectionStart; onChange(e.target.value); }}
        onSelect={(e) => { caret.current = e.target.selectionStart; }}
        className="text-sm" data-testid={`sms-template-input-${kind}`} />
      <div className="flex flex-wrap items-center gap-1.5">
        {SMS_VARS_BY_KIND[kind].map((v) => (
          <button key={v} type="button" disabled={disabled} onClick={() => insert(v)}
            className="rounded-full border px-2.5 py-0.5 text-xs text-primary hover:bg-primary/5 disabled:opacity-50"
            data-testid={`sms-var-${kind}-${v}`}>
            + {VARS[v].label}
          </button>
        ))}
        {value && value !== defaultText && (
          <Button type="button" variant="ghost" size="sm" className="h-6 text-xs" disabled={disabled}
            onClick={() => onChange(null)} data-testid={`sms-template-reset-${kind}`}>
            <RotateCcw className="h-3 w-3 mr-1" /> Texte par défaut
          </Button>
        )}
      </div>
      <div className="rounded-md bg-muted/50 p-2.5 text-xs" data-testid={`sms-preview-${kind}`}>
        <span className="font-medium text-muted-foreground">Aperçu : </span>{preview}
        <span className="block mt-1 text-muted-foreground" data-testid={`sms-count-${kind}`}>
          {preview.length} caractères · {smsCount(preview)} SMS
        </span>
      </div>
    </div>
  );
};
